import * as React from 'react';
import { useStateCallback, useStyles, removeIndex, addItem, isDefined } from '@sravni/react-utils';
import { ACCORDION_SIZES } from '../../../constants/sizes.js';
import { Space } from '../../Space/src/Space.js';
import modules_127cd76a from '../styles/Accordion.module.less.js';
import AccordionContext from './AccordionContext.js';
import '../types/Accordion.types.js';

/** Helper function normalizes indexes */
const normalizeIndexes = (children, index) => {
    if (Array.isArray(index)) {
        return index;
    }
    if (index === '*') {
        const index = React.Children.map(children, (_, i) => i) || [];
        return index;
    }
    return [];
};
/**
 * @description A content area which can be collapsed and expanded
 *
 * @component
 * @example
 * ```jsx
 * <Accordion activeKeys={['credit']}>
 *   <Accordion.Item key="credit" title="О кредите">content</Accordion.Item>
 * </Accordion>
 * ```
 */
const Accordion = React.forwardRef((props, ref) => {
    const { index, defaultIndex, separator, background, padding, scrollIntoView, children, className, onChange, size = ACCORDION_SIZES.S16, ...restProps } = props;
    const innerChildren = React.useMemo(() => React.Children.toArray(children).filter(Boolean), [children]);
    const isFirstRender = React.useRef(true);
    const [innerValue, setInnerValue] = useStateCallback(normalizeIndexes(innerChildren, index ?? defaultIndex));
    const cx = useStyles(modules_127cd76a);
    const classNames = cx(className, {
        [`separator-${separator}`]: separator,
        [`background-${background}`]: background,
    });
    const handleCollapse = React.useCallback((event) => {
        const value = Number(event.currentTarget.dataset.index);
        const pos = innerValue.indexOf(value);
        const indexes = pos >= 0 ? removeIndex(innerValue, pos) : addItem(innerValue, value);
        setInnerValue(indexes, (val) => {
            onChange?.(val || []);
        });
    }, [innerValue, onChange, setInnerValue]);
    const panels = React.useMemo(() => React.Children.map(innerChildren, (child, index) => {
        const childProps = {
            ...child.props,
            id: index,
            active: innerValue.indexOf(index) >= 0,
        };
        return React.cloneElement(child, childProps);
    }), [innerValue, innerChildren]);
    const accordionContextProviderValue = React.useMemo(() => ({ scrollIntoView, padding, onCollapse: handleCollapse, size }), [handleCollapse, padding, scrollIntoView, size]);
    const separatorSpace = separator === 'space' ? accordionContextProviderValue.size : 0;
    React.useEffect(() => {
        if (isFirstRender.current) {
            const indexes = normalizeIndexes(innerChildren, index ?? defaultIndex);
            setInnerValue(indexes);
            isFirstRender.current = false;
        }
        else {
            setInnerValue((prevIndex) => (isDefined(index) ? normalizeIndexes(innerChildren, index) : prevIndex));
        }
    }, [innerChildren, index, defaultIndex, setInnerValue]);
    return (React.createElement(Space, { ref: ref, "data-qa": "Accordion", size: separatorSpace, direction: "vertical", className: classNames, ...restProps },
        React.createElement(AccordionContext.Provider, { value: accordionContextProviderValue }, panels)));
});
Accordion.defaultProps = {
    separator: 'space',
    scrollIntoView: true,
    padding: true,
};
Accordion.displayName = 'Accordion';

export { Accordion };
//# sourceMappingURL=Accordion.js.map
